import { Box, Stack, TextField } from "@mui/material";
import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { SettingsPageHeader, FloatingSaveButton } from "../components";
import { OptionsManager } from "../components/OptionsManager";
import { RecordEditDialog } from "../components/RecordEditDialog";
import { clinicianIdSchema, clinicianIdDuplicateCriteria, ClinicianIdRecord } from "../components/schemas";
import { useClinicianIds, useRecordEditor } from "../hooks";

const ClinicianIdsSettings = () => {
  const auth = useAuth();
  const currentUserIdentityId = String(auth.user?.profile?.oid || "");

  const {
    entries,
    setEntries,
    fallbackId,
    setFallbackId,
    isDirty,
    isSaving,
    save,
    isLoading,
  } = useClinicianIds();

  const {
    editingRecord,
    handleEdit,
    handleEditSave,
    handleEditCancel,
    handleDelete,
  } = useRecordEditor<ClinicianIdRecord>({
    setRecords: setEntries,
    displayField: "clinicianId",
  });

  // Custom schema with current user's identity ID as default
  const customSchema = useMemo((): typeof clinicianIdSchema => ({
    ...clinicianIdSchema,
    defaultValues: {
      ...clinicianIdSchema.defaultValues,
      identityId: currentUserIdentityId || "",
    },
  }), [currentUserIdentityId]);

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          <SettingsPageHeader title="Clinician IDs" isDirty={isDirty} />

          <OptionsManager
            records={entries}
            onChange={setEntries}
            schemaDefinition={customSchema}
            duplicateRecordCriteria={clinicianIdDuplicateCriteria}
            title="Clinician IDs"
            useAccordion={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={isLoading}
          />

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Fallback Clinician ID"
              value={fallbackId || ""}
              onChange={(e) => setFallbackId(e.target.value)}
              variant="outlined"
              helperText="This clinician ID will be used when no matching clinician ID is found for a user's identity ID"
              placeholder="Enter fallback clinician ID"
              sx={{ maxWidth: 500 }}
            />
          </Box>
        </Stack>

        <RecordEditDialog
          open={editingRecord !== null}
          onClose={handleEditCancel}
          onSave={handleEditSave}
          record={editingRecord}
          schemaDefinition={customSchema}
        />

        <FloatingSaveButton show={isDirty} onSave={save} isSaving={isSaving} />
      </Box>
    </Box>
  );
};

export { ClinicianIdsSettings };
